import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import type { RegistryDevice, UpsertRegistryDeviceRequest } from "@/types";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

interface CreateDeviceResponse {
  id: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  company_id: number;
  device_eic: string;
  device_name: string;
  device_power: number;
  support: boolean | null;
  registry_status: string;
  technology_code: string;
  technology: string | null;
  commercial_operation_date: string | null;
  projection_multiplier: number | null;
  ecolabel_approved: boolean | null;
  address: string | null;
  country_specific_metadata?: Record<string, unknown> | null;
  production_estimate_override?: {
    format: number;
    monthly_estimate_mwh: number[];
    updated_at: string;
  } | null;
}

export const useRegistryDevicesStore = defineStore("registryDevices", () => {
  const registryDevices = ref<RegistryDevice[]>([]);
  const isLoading = ref(false);
  const isCreating = ref(false);
  const error = ref<string | null>(null);

  // Current company context
  const currentCompanyId = ref<number | null>(null);

  // Computed properties
  const hasDevices = computed((): boolean => {
    return registryDevices.value.length > 0;
  });

  const totalDevicePower = computed((): number => {
    return registryDevices.value.reduce((total, device) => {
      return total + (device.device_power || 0);
    }, 0);
  });

  // Actions
  const fetchRegistryDevices = async (companyId?: number): Promise<void> => {
    if (!companyId) {
      return;
    }

    const { globalErrorHandler } = useGlobalHandlers();

    try {
      isLoading.value = true;
      error.value = null;
      currentCompanyId.value = companyId;

      const response = await goSolidApi.get<RegistryDevice[]>(
        `/api/company/${companyId}/registry-devices`
      );

      registryDevices.value = response.data;
    } catch (err: any) {
      error.value =
        err.response?.data?.error ||
        err.message ||
        "Failed to fetch registry devices";
      globalErrorHandler(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const upsertRegistryDevice = async (
    companyId: number | undefined,
    deviceData: UpsertRegistryDeviceRequest
  ): Promise<CreateDeviceResponse> => {
    const { globalErrorHandler } = useGlobalHandlers();

    try {
      isCreating.value = true;
      error.value = null;

      const response = await goSolidApi.post<CreateDeviceResponse>(
        `/api/company/${companyId}/registry-devices`,
        deviceData
      );

      // Convert the response to match RegistryDevice interface
      const newDevice: RegistryDevice = {
        id: response.data.id,
        company_id: response.data.company_id,
        device_eic: response.data.device_eic,
        device_name: response.data.device_name,
        device_power: response.data.device_power,
        support: response.data.support?.toString() || "",
        certificates: [],
        registry_status: response.data.registry_status,
        technology_code: response.data.technology_code,
        technology: response.data.technology || "",
        commercial_operation_date: response.data.commercial_operation_date,
        ecolabel_approved: response.data.ecolabel_approved,
        address: response.data.address ?? null,
        country_specific_metadata: response.data.country_specific_metadata,
      };

      // Check if device already exists and update it, otherwise add new device
      const existingDeviceIndex = registryDevices.value.findIndex(
        (device) => device.device_eic === response.data.device_eic
      );

      if (existingDeviceIndex !== -1) {
        // Update existing device
        registryDevices.value[existingDeviceIndex] = {
          ...registryDevices.value[existingDeviceIndex],
          ...newDevice,
        };
      } else {
        // Add new device to the store
        registryDevices.value.unshift(newDevice);
      }

      return response.data;
    } catch (err: any) {
      // Handle specific error cases
      if (err.response?.status === 403) {
        error.value =
          "Manual device entry is only available for Polish companies";
      } else if (err.response?.status === 409) {
        error.value =
          err.response?.data?.message || "Device EIC already exists";
      } else if (err.response?.status === 400) {
        error.value = "Invalid device data provided";
      } else {
        error.value =
          err.response?.data?.error || err.message || "Failed to upsert device";
      }
      globalErrorHandler(err);
      throw err;
    } finally {
      isCreating.value = false;
    }
  };

  const clearError = (): void => {
    error.value = null;
  };

  const reset = (): void => {
    registryDevices.value = [];
    currentCompanyId.value = null;
    error.value = null;
    isLoading.value = false;
    isCreating.value = false;
  };

  const setProductionOverride = async (
    companyId: number,
    deviceId: number,
    monthlyEstimateMwh: number[] | null
  ): Promise<{
    feasibility: {
      feasible: boolean;
      details?: {
        period_label: string;
        shortfall_mwh: number;
        demand_mwh: number;
      };
    } | null;
  }> => {
    error.value = null;

    try {
      const response = await goSolidApi.put<{
        data: CreateDeviceResponse;
        feasibility: {
          feasible: boolean;
          details?: {
            period_label: string;
            shortfall_mwh: number;
            demand_mwh: number;
          };
        } | null;
      }>(
        `/api/company/${companyId}/registry-devices/${deviceId}/production-estimate-override`,
        { monthly_estimate_mwh: monthlyEstimateMwh }
      );
      await fetchRegistryDevices(companyId);
      return { feasibility: response.data.feasibility };
    } catch (err: any) {
      error.value =
        err.response?.data?.error ||
        err.message ||
        "Failed to set production estimate override";
      throw err;
    }
  };

  const fetchDeviceProjectionDetails = async (
    companyId: number,
    deviceIds: number[]
  ): Promise<
    Record<
      number,
      {
        projection: Record<string, number>;
        auto_projection: Record<string, number>;
        has_override: boolean;
      }
    >
  > => {
    if (deviceIds.length === 0) return {};
    const response = await goSolidApi.get(
      `/api/company/${companyId}/registry-devices/projection-details`,
      { params: { device_ids: deviceIds.join(",") } }
    );
    return response.data.data;
  };

  // Filter devices by exclusion list (for compatibility with existing component)
  const getFilteredDevices = (excludedDevices?: string[]): RegistryDevice[] => {
    if (!excludedDevices || excludedDevices.length === 0) {
      return registryDevices.value;
    }

    const blacklist = excludedDevices.map((str) => str.trim());
    return registryDevices.value.filter(
      (device) => !blacklist.includes(device.device_eic)
    );
  };

  return {
    // State
    registryDevices,
    isLoading,
    isCreating,
    error,
    currentCompanyId,

    // Computed properties
    hasDevices,
    totalDevicePower,

    // Actions
    fetchRegistryDevices,
    upsertRegistryDevice,
    clearError,
    reset,
    getFilteredDevices,
    setProductionOverride,
    fetchDeviceProjectionDetails,
  };
});
